/**
 * API agenda datetimes are wall-clock strings (YYYY-MM-DD HH:mm:ss), not UTC offsets.
 * FullCalendar must receive ISO local form (T separator, no Z) to avoid UTC shift.
 */
export const toCalendarEventDate = (datetime: string): string =>
  datetime.trim().replace(" ", "T");

/**
 * Adds minutes to a wall-clock agenda datetime, returning FullCalendar's local ISO form.
 * Used only for visual overlays (e.g. post-service buffer block) — never persisted.
 */
export const addWallClockMinutes = (datetime: string, minutes: number): string => {
  const normalized = toCalendarEventDate(datetime);

  if (!minutes) {
    return normalized;
  }

  const base = new Date(normalized);

  if (Number.isNaN(base.getTime())) {
    return normalized;
  }

  base.setMinutes(base.getMinutes() + minutes);

  const year = base.getFullYear();
  const month = String(base.getMonth() + 1).padStart(2, "0");
  const day = String(base.getDate()).padStart(2, "0");
  const hours = String(base.getHours()).padStart(2, "0");
  const mins = String(base.getMinutes()).padStart(2, "0");
  const seconds = String(base.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${mins}:${seconds}`;
};

export const formatWallClockTime = (datetime: string): string => datetime.slice(11, 16);

export const wallClockDatePart = (datetime: string): string => datetime.slice(0, 10);

export const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/** Wall-clock datetime for API payloads from a local `Date`. */
export const formatWallClockDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const isSlotInPast = (startDatetime: string): boolean => {
  const normalized = toCalendarEventDate(startDatetime);
  const slotTime = new Date(normalized).getTime();

  if (Number.isNaN(slotTime)) {
    return false;
  }

  return slotTime < Date.now();
};

export const parseTimeToMinutes = (time: string): number => {
  const parts = time.split(":");

  return Number(parts[0] ?? 0) * 60 + Number(parts[1] ?? 0);
};

export const minutesToTimeLabel = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
};

export type ScheduleWindow = {
  start_time: string;
  end_time: string;
};

export const DEFAULT_SLOT_MIN = "08:00:00";
export const DEFAULT_SLOT_MAX = "20:00:00";
